import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LogOut, Eye, EyeOff, User, Mail, Phone, Briefcase, MapPin, Save, Lock, Globe, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { useTheme } from "@/lib/theme";
import NotificationsPanel from "@/components/NotificationsPanel";

const MOBILE_THEMES = [
  { id: "sage",   name: "Sage Calm",   emoji: "🌿", desc: "Warm & calming · light" },
  { id: "carbon", name: "Carbon Dark", emoji: "⚫", desc: "High-contrast · dark" },
];

const LANGUAGES = [{ code: "en-US", label: "English (US)" }];
const COUNTRIES = [
  { code: "US", label: "USA" },
  { code: "CA", label: "Canada" },
  { code: "GB", label: "United Kingdom" },
  { code: "IN", label: "India" },
  { code: "AU", label: "Australia" },
];
const TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Toronto", "America/Vancouver", "Europe/London", "Europe/Berlin",
  "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney", "UTC",
];

function initials(name) {
  return (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}

function SectionCard({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl shadow-soft p-5" data-testid={testid}>
      <div className="flex items-start gap-3 mb-4">
        {Icon && <div className="w-10 h-10 rounded-xl bg-brand-100 text-brand-700 flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>}
        <div>
          <h3 className="font-display font-semibold text-stone-900 text-lg">{title}</h3>
          {subtitle && <p className="text-stone-500 text-xs mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function FieldInput({ label, icon: Icon, ...rest }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />}{label}
      </label>
      <input {...rest} className={`w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm ${rest.disabled ? "bg-stone-50 text-stone-500" : ""}`} />
    </div>
  );
}

function PasswordField({ label, value, onChange, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">{label}</label>
      <div className="relative">
        <input type={show ? "text" : "password"} value={value} onChange={onChange} data-testid={testid}
          className="w-full px-3 py-2.5 pr-10 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm" />
        <button type="button" onClick={() => setShow((v) => !v)} data-testid={`${testid}-eye`} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-stone-500 hover:text-stone-800">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export default function MobileSettings() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { theme: themeId, setTheme } = useTheme();

  const [profile, setProfile] = useState({
    full_name: user?.full_name || "",
    phone: user?.phone || "",
    address: user?.address || "",
    specialty: user?.specialty || "",
  });
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [region, setRegion] = useState({
    language: user?.language || "en-US",
    country: user?.country || "US",
    timezone: user?.timezone || (Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"),
  });
  const [feedback, setFeedback] = useState({ feedback_type: "Feature Request", message: "" });
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2800); };

  const saveProfile = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.patch("/auth/me", profile); flash("Profile saved"); } finally { setBusy(false); }
  };
  const updatePassword = async (e) => {
    e.preventDefault();
    if (pwd.next.length < 8) return flash("New password must be at least 8 characters");
    if (pwd.next !== pwd.confirm) return flash("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      setPwd({ current: "", next: "", confirm: "" });
      flash("Password updated");
    } catch (err) {
      flash(err.response?.data?.detail || "Could not update password");
    } finally { setBusy(false); }
  };
  const saveRegion = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.patch("/auth/me", region); flash("Regional settings saved"); } finally { setBusy(false); }
  };
  const sendFeedback = async (e) => {
    e.preventDefault();
    if (!feedback.message.trim()) return flash("Please tell us what's on your mind");
    setBusy(true);
    try {
      await api.post("/feedback", feedback);
      setFeedback({ feedback_type: "Feature Request", message: "" });
      flash("Thanks — feedback sent!");
    } finally { setBusy(false); }
  };

  const detectedZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "—";

  return (
    <div className="max-w-md mx-auto pb-4 bg-stone-50 min-h-screen" data-testid="mobile-settings">
      <header className="px-5 pt-6 pb-3">
        <h1 className="font-display text-2xl font-bold text-stone-900">Settings</h1>
        <p className="text-stone-500 text-sm">{user?.email}</p>
      </header>

      <div className="px-5 space-y-6 pb-6">
        {/* Profile information */}
        <form onSubmit={saveProfile} className="contents">
          <SectionCard icon={User} title="Profile information" subtitle="Update your contact details." testid="section-profile">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-bold text-lg">{initials(profile.full_name)}</div>
              <div>
                <div className="font-semibold text-stone-900">{profile.full_name || "—"}</div>
                <div className="text-xs text-stone-500 capitalize">{user?.role}</div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FieldInput label="Full name" icon={User} data-testid="profile-fullname" value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} />
                <FieldInput label="Email (read-only)" icon={Mail} value={user?.email || ""} disabled />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FieldInput label="Phone" icon={Phone} data-testid="profile-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
                <FieldInput label="Specialty" icon={Briefcase} data-testid="profile-specialty" value={profile.specialty} onChange={(e) => setProfile({ ...profile, specialty: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />Address</label>
                <textarea rows={2} value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} data-testid="profile-address"
                  className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm resize-none" />
              </div>
              <div className="flex justify-end">
                <button disabled={busy} data-testid="save-profile" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-brand-500 text-white font-semibold text-sm disabled:opacity-60">
                  <Save className="w-4 h-4" />Save changes
                </button>
              </div>
            </div>
          </SectionCard>
        </form>

        {/* Change password */}
        <form onSubmit={updatePassword} className="contents">
          <SectionCard icon={Lock} title="Change password" subtitle="Use at least 8 characters. Mix letters, numbers and symbols for the strongest security." testid="section-password">
            <div className="space-y-3">
              <PasswordField label="Current password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} testid="pwd-current" />
              <PasswordField label="New password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} testid="pwd-new" />
              <PasswordField label="Confirm new password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} testid="pwd-confirm" />
              {pwd.confirm && pwd.next !== pwd.confirm && (
                <div className="text-xs text-rose-600">Passwords do not match.</div>
              )}
              <div className="flex justify-end">
                <button disabled={busy || !pwd.current || !pwd.next} data-testid="update-password" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-brand-500 text-white font-semibold text-sm disabled:opacity-60">
                  <Lock className="w-4 h-4" />Update password
                </button>
              </div>
            </div>
          </SectionCard>
        </form>

        {/* Regional settings */}
        <form onSubmit={saveRegion} className="contents">
          <SectionCard icon={Globe} title="Regional settings" subtitle="Choose your language, country and time zone. Calendar times will display in your selected zone." testid="section-region">
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Language</label>
                <select value={region.language} onChange={(e) => setRegion({ ...region, language: e.target.value })} data-testid="region-language"
                  className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm">
                  {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
                <div className="text-[11px] text-stone-400 mt-1">More languages coming soon.</div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Country</label>
                <select value={region.country} onChange={(e) => setRegion({ ...region, country: e.target.value })} data-testid="region-country"
                  className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm">
                  {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Time zone</label>
                <select value={region.timezone} onChange={(e) => setRegion({ ...region, timezone: e.target.value })} data-testid="region-timezone"
                  className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm">
                  {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </select>
                <div className="text-[11px] text-stone-400 mt-1">Detected browser zone: {detectedZone}</div>
              </div>
              <div className="flex justify-end">
                <button disabled={busy} data-testid="save-region" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-brand-500 text-white font-semibold text-sm disabled:opacity-60">
                  <Save className="w-4 h-4" />Save settings
                </button>
              </div>
            </div>
          </SectionCard>
        </form>

        {/* Push notifications */}
        <NotificationsPanel compact />

        {/* Theme picker */}
        <SectionCard title="Appearance" subtitle="Two curated themes." testid="section-theme">
          <div className="grid grid-cols-2 gap-3">
            {MOBILE_THEMES.map((t) => (
              <button key={t.id} onClick={() => setTheme(t.id)} data-testid={`theme-${t.id}`}
                className={`bg-white border rounded-2xl p-3 text-left transition ${themeId === t.id ? "border-brand-500 ring-2 ring-brand-100" : "border-stone-200"}`}>
                <div className="text-2xl">{t.emoji}</div>
                <div className="font-semibold text-stone-900 mt-1">{t.name}</div>
                <div className="text-[10px] text-stone-500">{t.desc}</div>
                {themeId === t.id && <div className="text-[10px] text-brand-700 font-semibold mt-1">Active</div>}
              </button>
            ))}
          </div>
        </SectionCard>

        {/* Send us feedback */}
        <form onSubmit={sendFeedback} className="contents">
          <SectionCard icon={MessageSquare} title="Send us feedback" subtitle="Your message will be emailed to the application owner. We read everything." testid="section-feedback">
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Feedback type *</label>
                <select value={feedback.feedback_type} onChange={(e) => setFeedback({ ...feedback, feedback_type: e.target.value })} data-testid="feedback-kind"
                  className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm">
                  <option value="Feature Request">Feature Request</option>
                  <option value="Bug Report">Bug Report</option>
                  <option value="General Feedback">General Feedback</option>
                  <option value="Enhancement">Enhancement</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <textarea rows={4} placeholder="Tell us what's on your mind…" value={feedback.message}
                onChange={(e) => setFeedback({ ...feedback, message: e.target.value.slice(0, 5000) })}
                data-testid="feedback-message"
                className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm resize-none" />
              <div className="flex justify-end">
                <button disabled={busy || !feedback.message.trim()} data-testid="send-feedback" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-brand-500 text-white font-semibold text-sm disabled:opacity-60">
                  <MessageSquare className="w-4 h-4" />Send feedback
                </button>
              </div>
            </div>
          </SectionCard>
        </form>

        <button onClick={() => { logout(); nav("/login"); }} className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white border border-stone-200 text-stone-700 font-semibold hover:bg-stone-50" data-testid="mobile-logout">
          <LogOut className="w-4 h-4" />Sign out
        </button>
      </div>

      {toast && <div className="fixed left-1/2 -translate-x-1/2 bottom-24 px-4 py-2 rounded-full bg-stone-900 text-white text-sm shadow-lg z-40" data-testid="settings-toast">{toast}</div>}
    </div>
  );
}
